import { createContext, useContext, useState } from 'react';
import { Button } from '@/components/ui/button';
import { activeEvents } from './projection';
import type { ClinicalEvent } from './types';
export interface MedicationLot {
  drug: string;
  lot: string;
  expiration?: string | null;
  recorded_for_administration_at: string;
}
export const MedicationLotContext = createContext<{
  lookup?: (drug: string) => Promise<MedicationLot[]>;
  events: ClinicalEvent[];
}>({ events: [] });
export function localMedicationLots(events: ClinicalEvent[], drug: string, now = Date.now()): MedicationLot[] {
  return activeEvents(events)
    .filter(
      (e) =>
        Date.parse(e.occurred_at) <= now &&
        e.event_type === 'MED.ADMIN' &&
        e.payload.drug === drug &&
        e.payload.status === 'Administered' &&
        typeof e.payload.lot === 'string',
    )
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))
    .map((e) => ({
      drug,
      lot: e.payload.lot as string,
      expiration: e.payload.expiration as string | undefined,
      recorded_for_administration_at: e.occurred_at,
    }));
}
export function MedicationLotSuggestions({ drug, onApply }: { drug: string; onApply: (lot: MedicationLot) => void }) {
  const context = useContext(MedicationLotContext);
  const [found, setFound] = useState<{ drug: string; lots: MedicationLot[] }>(),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false);
  async function find() {
    setBusy(true);
    setMessage('');
    const local = localMedicationLots(context.events, drug);
    try {
      const lots = context.lookup && navigator.onLine ? await context.lookup(drug) : local;
      setFound({ drug, lots: [...new Map(lots.map((l) => [`${l.lot}:${l.expiration}`, l])).values()] });
      if (!lots.length) setMessage('No previous confirmed lot is available. Enter the package details directly.');
    } catch {
      setFound({ drug, lots: local });
      setMessage('Practice lookup unavailable. Only confirmed administrations cached in this episode are shown.');
    } finally {
      setBusy(false);
    }
  }
  if (!drug) return null;
  return (
    <div className="flex flex-col gap-2">
      <Button type="button" variant="outline" className="min-h-11 self-start" disabled={busy} onClick={find}>
        Find previous lots for {drug}
      </Button>
      {found?.drug === drug &&
        found.lots.map((lot, index) => (
          <Button
            type="button"
            variant="outline"
            className="min-h-11 h-auto whitespace-normal justify-start"
            key={index}
            onClick={() => onApply(lot)}>
            Use lot {lot.lot}
            {lot.expiration ? ` · expires ${lot.expiration}` : ''} (verify against package)
          </Button>
        ))}
      {message && (
        <p role="status" className="text-sm text-muted-foreground">
          {message}
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        Choosing a lot fills package fields only; it does not record administration, dose or route.
      </p>
    </div>
  );
}
